import React from "react";
import { listToGroupList, isDefined } from "@togglecorp/fujs";
import { FaramListElement } from "@togglecorp/faram";

import ListItem from "./ListItem";
import GroupItem from "./GroupItem";

export type ListDataItem =
  | string
  | number
  | { dummy?: string; [key: string]: unknown }
  | readonly unknown[];

interface NormalListProps<
  D = ListDataItem,
  K extends React.Key = string | number,
> {
  data: readonly D[];
  keySelector?: (item: D, index: number) => K;
  groupKeySelector?: (item: D) => string | number;
  groupComparator?: (a: string | number, b: string | number) => number;
  renderer?: React.ComponentType<
    React.PropsWithChildren<{ className?: string } & object>
  >;
  rendererClassName?: string;
  rendererParams?: (
    key: K,
    item: D,
    index: number,
    data: readonly D[]
  ) => object;
  modifier?: (
    key: K,
    item: D,
    index: number,
    data: readonly D[]
  ) => React.ReactNode;
  groupRenderer?: React.ComponentType<
    React.PropsWithChildren<{ className?: string } & object>
  >;
  groupRendererClassName?: string;
  groupRendererParams?: (groupKey: string | number) => object;

  [key: string]: unknown;
}

const NormalListBase = <
  D = ListDataItem,
  K extends React.Key = string | number,
>(
  props: NormalListProps<D, K>
) => {
  const {
    data,
    keySelector,
    groupKeySelector,
    groupComparator,
    renderer: Renderer,
    rendererClassName,
    rendererParams,
    modifier,
    groupRenderer: GroupRenderer = GroupItem,
    groupRendererClassName,
    groupRendererParams,
  } = props;

  const renderItem = (item: D, index: number): React.ReactNode => {
    const key: K = isDefined(keySelector)
      ? keySelector(item, index)
      : (item as unknown as K);

    if (modifier) return modifier(key, item, index, data);

    if (Renderer) {
      const extra = rendererParams
        ? rendererParams(key, item, index, data)
        : {};
      return <Renderer className={rendererClassName} key={key} {...extra} />;
    }

    return <ListItem key={key}>{String(item)}</ListItem>;
  };

  if (!groupKeySelector) {
    return <>{data.map(renderItem)}</>;
  }

  const groups = listToGroupList(
    [...data] as D[],
    groupKeySelector as (item: D, index: number) => number,
    renderItem
  );

  const keys = Object.keys(groups) as (string | number)[];
  if (groupComparator) keys.sort(groupComparator);

  const children: React.ReactNode[] = [];
  keys.forEach((groupKey) => {
    const extra = groupRendererParams ? groupRendererParams(groupKey) : {};
    children.push(
      <GroupRenderer
        key={groupKey}
        className={groupRendererClassName}
        {...extra}
      />
    );
    children.push(...groups[groupKey]);
  });

  return <>{children}</>;
};

const NormalList = FaramListElement(NormalListBase);
export default NormalList;
